import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Download, TerminalSquare } from "lucide-react";
import { NAV_LINKS } from "@/data/content";
import { scrollToSection } from "@/lib/scroll";

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        scrollToSection(id);
    };

    return (
        <header
            className={`fixed inset-x-0 top-0 z-40 border-b backdrop-blur-xl transition-[background-color,border-color] duration-500 ${
                scrolled
                    ? "border-white/10 bg-black/70"
                    : "border-white/5 bg-black/40"
            }`}
        >
            <nav
                className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8"
                aria-label="Primary navigation"
            >
                <button
                    type="button"
                    data-testid="nav-logo"
                    onClick={() => go("home")}
                    className="group flex items-center gap-3"
                    aria-label="Harshdeep Singh — back to top"
                >
                    <span className="h-9 w-9 overflow-hidden border border-neon/50 transition-[border-color,box-shadow] duration-300 group-hover:border-neon group-hover:shadow-[0_0_16px_rgba(0,255,102,0.25)]">
                        <img
                            src="/profile.webp"
                            alt="Harshdeep Singh"
                            className="h-full w-full object-cover object-top"
                        />
                    </span>
                    <span className="hidden font-display text-sm font-bold tracking-wide text-zinc-100 sm:block">
                        HARSHDEEP SINGH
                    </span>
                </button>

                <ul className="hidden items-center gap-7 lg:flex">
                    {NAV_LINKS.map((link) => (
                        <li key={link.id}>
                            <button
                                type="button"
                                data-testid={`nav-${link.id}-link`}
                                onClick={() => go(link.id)}
                                className="font-mono2 text-[13px] text-zinc-400 transition-colors duration-300 hover:text-neon"
                            >
                                {link.label}
                            </button>
                        </li>
                    ))}
                </ul>

                <div className="flex items-center gap-3">
                    <a
                        href="/resume.pdf"
                        download
                        data-testid="nav-download-resume-btn"
                        className="hidden items-center gap-2 border border-neon px-4 py-2 font-mono2 text-[13px] text-neon transition-colors duration-300 hover:bg-neon hover:text-black sm:flex"
                    >
                        <Download size={14} strokeWidth={1.5} />
                        Download Resume
                    </a>
                    <button
                        type="button"
                        data-testid="nav-mobile-menu-btn"
                        onClick={() => setOpen((v) => !v)}
                        className="flex h-10 w-10 items-center justify-center border border-white/10 text-zinc-300 transition-colors duration-300 hover:border-neon/50 hover:text-neon lg:hidden"
                        aria-label={open ? "Close menu" : "Open menu"}
                        aria-expanded={open}
                    >
                        {open ? <X size={18} /> : <Menu size={18} />}
                    </button>
                </div>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-white/5 bg-black/95 backdrop-blur-xl lg:hidden"
                    >
                        <ul className="space-y-1 px-6 py-6">
                            {NAV_LINKS.map((link) => (
                                <li key={link.id}>
                                    <button
                                        type="button"
                                        data-testid={`nav-mobile-${link.id}-link`}
                                        onClick={() => go(link.id)}
                                        className="flex w-full items-center gap-3 py-2.5 text-left font-mono2 text-sm text-zinc-300 transition-colors duration-300 hover:text-neon"
                                    >
                                        <TerminalSquare size={14} className="text-neon/60" strokeWidth={1.5} />
                                        {link.label}
                                    </button>
                                </li>
                            ))}
                            <li className="pt-3">
                                <a
                                    href="/resume.pdf"
                                    download
                                    data-testid="nav-mobile-download-resume-btn"
                                    className="flex items-center justify-center gap-2 border border-neon px-4 py-3 font-mono2 text-sm text-neon transition-colors duration-300 hover:bg-neon hover:text-black"
                                >
                                    <Download size={14} strokeWidth={1.5} />
                                    Download Resume
                                </a>
                            </li>
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};
